import { useEffect } from "react";
import { post } from "../Services/api.service";
import { Paths } from "../Services/EndPoints";
import { deriveTrafficSource, readStoredUtm } from "../Utils/trafficSource";
import { ensureGuestSessionToken } from "../Utils/HelperFunctions";

// Records ONE storefront landing visit per session, tagged with its traffic
// source, for the seller's Insights ("where do my visits come from"). Call it
// only from the pages a shopper actually lands on from a share/ad — the store
// home, a PDP, and a sub-category listing. The store is resolved server-side
// from request context; the session rides the session_token header. Best-effort —
// it never blocks or errors the storefront.

// Two markers, not one.
//   CONFIRMED — written only once the POST actually comes back. It used to be
//     written on the line straight after firing the request, so a failed write
//     meant the shopper recorded NO visit for the rest of her session and nothing
//     ever retried.
//   PENDING — written synchronously before the request goes out, and removed the
//     moment it settles. It exists only to cover the in-flight window: without it
//     a slow request plus fast navigation (home -> PDP) fires two visits while
//     neither has confirmed yet, which is a real double-count now that the read
//     query gives a logged-in shopper a stable per-customer key.
// So: success -> CONFIRMED, never fires again this session. Failure -> both
// markers clear, and the next page the shopper opens tries again.
const CONFIRMED_KEY = "wc_visit_recorded";
const PENDING_KEY = "wc_visit_pending";

// sessionStorage can be unavailable (private mode) or vanish mid-flight; a
// tracking marker is never worth throwing on.
const safeSet = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    /* best-effort */
  }
};

const safeRemove = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    /* best-effort */
  }
};

export default function useRecordStoreVisit() {
  useEffect(() => {
    try {
      if (
        !Paths.store_visit ||
        sessionStorage.getItem(CONFIRMED_KEY) ||
        sessionStorage.getItem(PENDING_KEY)
      )
        return;
      // MINT THE GUEST SESSION TOKEN BEFORE POSTING, or the visit is filed under no session at
      // all and the shopper's own cart and order can never be linked back to it.
      //
      // api.service attaches whatever is in sessionStorage AT THE MOMENT OF THE CALL. The token
      // is otherwise created by App.js, inside an effect keyed on [storeDetails, user] — so it
      // waits for the store-details request. This hook fires on page mount and does not. Which
      // one wins is a race, and it is decided by how much data the landing page loads first:
      // landing on a PDP the token existed, landing on the store home it did not.
      //
      // When it lost, the visit row was written with a NULL session, so the funnel keyed it
      // 'row:<id>' — a real visitor that nothing downstream can ever match. The shopper then
      // got her token moments later, her cart keyed on it, and the funnel reported visits
      // without a single cart addition behind them.
      //
      // Guests only: a logged-in shopper is sent session_token: "" by design, and App.js
      // deliberately removes the token for her, so minting one here would fight that.
      try {
        const raw = localStorage.getItem("userData");
        const loggedIn = !!raw && Object.keys(JSON.parse(raw) || {}).length > 0;
        if (!loggedIn) ensureGuestSessionToken();
      } catch (e) {
        /* storage unreadable — fall through and post without a session, as before */
      }
      const params = new URLSearchParams(window.location.search);
      // All three come from the cookie captureUtm() wrote at app boot, falling back to
      // deriving the source here if the cookie is unavailable. This MUST be the same set
      // the order-create calls attach via readStoredUtm(), otherwise a shopper who lands
      // from Instagram and later opens a WhatsApp link has her visit filed under WhatsApp
      // while her order is credited to Instagram — and the per-channel rows stop
      // reconciling with the orders beside them.
      //
      // Medium and campaign used to be read off window.location.search RIGHT HERE, on hook
      // mount. That is later than boot, and App.js explains why that matters: a
      // redirect-alias route can strip the query before this runs, so a tagged landing
      // silently recorded no campaign. Reading the cookie removes that race.
      const stored = readStoredUtm();
      const source =
        stored.utmSource || deriveTrafficSource(params, document.referrer);
      const shareId = params.get("s") || params.get("shareId") || "";
      const utmMedium = stored.utmMedium || "";
      const utmCampaign = stored.utmCampaign || "";
      const qs = new URLSearchParams({ source });
      if (shareId) qs.set("shareId", shareId);
      if (utmMedium) qs.set("utmMedium", utmMedium);
      if (utmCampaign) qs.set("utmCampaign", utmCampaign);
      safeSet(PENDING_KEY, "1");
      post(`${Paths.store_visit}?${qs.toString()}`, {})
        .then(() => safeSet(CONFIRMED_KEY, "1"))
        .catch(() => {})
        .finally(() => safeRemove(PENDING_KEY));
    } catch (e) {
      // best-effort; never affect the storefront
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
